import React from 'react';
import { FaCheckCircle } from 'react-icons/fa';

import DownloadApp from '../components/DownloadApp'; 
import TestimonialSlider from '../components/TestimonialSlider';
import BookingBanner from '../components/BookingBanner'; 
import FounderNote from '../components/FounderNote'; 
import Gallery from '../components/Gallery';
import HighlightsSection from '../components/HighlightsSection';

const About = () => {
  return (
    <div className="bg-slate-950">
      <section className="bg-slate-900 pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-white">About Us</h1>
          <div className="unique-line"></div>
        </div>
      </section>

      {/* Founder's Note Section */}
      <FounderNote />

      {/* New & Improved "Our Dedicated Team" Section */}
      <section className="bg-slate-900 py-16 mt-16 px-4 sm:px-6 lg:px-8">
        {/* The new location for the heading */}
        <div className="flex flex-col items-center mb-16">
          <h2 className="text-3xl font-bold text-amber-400 mb-2">
            Our Dedicated Team
          </h2>
          <div className="line-container relative w-24 h-1 overflow-hidden">
            <div className="line bg-black h-full w-full"></div>
          </div>
        </div>

        {/* Main content box */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side: Image */}
          <div className="relative rounded-lg overflow-hidden h-96">
            <img 
              src="assets/Group-Photo 2.png" 
              alt="Quick Wheels Team" 
              className="w-full h-full object-cover rounded-lg" 
            />
          </div>
          
          {/* Right side: Text content */}
          <div className="relative z-10 text-white">
            <p className="text-slate-400">
              Our incredible team works tirelessly every day, driven by a shared passion to make your car rental experience seamless and exceptional. It is their dedication and hard work that propels Quick Wheels forward, ensuring that we continue to provide the best service and a fleet of cars you can trust for every journey.
            </p>
          </div>
        </div>
      </section>

      {/* New "What Makes Us Special?" Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center mb-16">
            <h2 className="text-3xl font-bold text-amber-400 mb-2">
              What Makes Us Special?
            </h2>
            <div className="line-container relative w-24 h-1 overflow-hidden">
              <div className="line bg-black h-full w-full"></div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {/* High-Quality Cars */}
            <div className="bg-slate-900 rounded-lg p-8 shadow-lg transition-all duration-300 ease-in-out hover:scale-105">
              <h3 className="text-xl font-bold text-white mb-2 uppercase">High-Quality Cars</h3>
              <p className="text-slate-400 text-sm">
                We believe in providing a fleet that is a cut above the rest. All our vehicles undergo rigorous inspections and are meticulously maintained to ensure they are in pristine condition for your safety and comfort.
              </p>
            </div>
            
            {/* Transparency & Honesty */}
            <div className="bg-slate-900 rounded-lg p-8 shadow-lg transition-all duration-300 ease-in-out hover:scale-105">
              <h3 className="text-xl font-bold text-white mb-2 uppercase">Transparency & Honesty</h3>
              <p className="text-slate-400 text-sm">
                We pride ourselves on a straightforward and transparent rental process. No hidden fees, no surprises—just clear, honest pricing so you know exactly what you're paying for.
              </p>
            </div>
            
            {/* Exceptional Customer Service */}
            <div className="bg-slate-900 rounded-lg p-8 shadow-lg transition-all duration-300 ease-in-out hover:scale-105">
              <h3 className="text-xl font-bold text-white mb-2 uppercase">Exceptional Customer Service</h3>
              <p className="text-slate-400 text-sm">
                Our team is dedicated to your satisfaction. From the moment you book until you return the car, we are here to provide support and ensure your experience is as smooth and enjoyable as possible.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Add the new HighlightsSection here */}
      <HighlightsSection />

      {/* The new "Unlock unforgettable memories" section */}
      <section className="radial-gradient-bg py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Left side: Text and Features */}
          <div>
            <h2 className="text-4xl font-bold text-white leading-tight">
              Unlock unforgettable <span className="text-amber-400">memories on the road</span>
            </h2>
            <p className="text-slate-400 mt-4 text-sm">
              Phasellus non auctor fermentum sollicitudin. Eget adipiscing augue sit amet curae;
              viverra odio.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-8">
              <div className="flex items-start">
                <FaCheckCircle className="text-amber-400 mr-3 mt-1 flex-shrink-0" />
                <span className="text-slate-400">Velit proin mollis sed quam id nisl semper.</span>
              </div>
              <div className="flex items-start">
                <FaCheckCircle className="text-amber-400 mr-3 mt-1 flex-shrink-0" />
                <span className="text-slate-400">Purus non eu cursus porttitor tristique et gravida.</span>
              </div>
              <div className="flex items-start">
                <FaCheckCircle className="text-amber-400 mr-3 mt-1 flex-shrink-0" />
                <span className="text-slate-400">Aliquam adipiscing velit semper morbi.</span>
              </div>
              <div className="flex items-start">
                <FaCheckCircle className="text-amber-400 mr-3 mt-1 flex-shrink-0" />
                <span className="text-slate-400">Quis nunc interdum gravida ullamcorper.</span>
              </div>
            </div>
          </div>
          
          {/* Right Side: Unique Image Element */}
          <div className="relative rounded-lg overflow-hidden shadow-2xl transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-[0_0_50px_10px_rgba(251,191,36,0.5)]">
            <img 
              src="assets/memories-photo.png" 
              alt="Unforgettable memories" 
              className="w-full h-auto object-cover rounded-lg"
            />
          </div>
        </div>
      </section>

      <Gallery /> 
      <DownloadApp />
      <TestimonialSlider />
      <BookingBanner />

    </div>
  );
}

export default About;